import React, { createContext, useMemo, useContext, useState } from 'react';

import { UseQueryResult } from '@tanstack/react-query';

import { useAdminCampaignData } from '~/impact-measurement/pages/Admin/lib';
import { getStudyLevelStatus } from '~/utils/ipMatching';
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from '~/utils/pagination';

import {
  IPMatchingResponseType,
  useFetchIPMatching
} from '../Shared/hooks/useFetchIPMatching';

export type IPMatchedID = {
  ip_matching_type: string;
  ip_matching_id: string;
  enablement_status: string;
  is_enabled: boolean;
  added_at: string;
  lookup_key: string;
};

type IPMatching = {
  isLoadingIPMatching: boolean;
  campaign: AdminNavBarCampaign;
  ipMatching?: IPMatchingResponseType;
  rootLevelEnablement: boolean;
  matchedIds: IPMatchedID[];
  updatedIds?: IPMatchedID[];
  setUpdatedIds: React.Dispatch<React.SetStateAction<IPMatchedID[]>>;
  isRefetching: boolean;
  refetch: (
    options?: Record<string, any>
  ) => Promise<UseQueryResult<IPMatchingResponseType>>;
  status?: string;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  pageSize: number;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  campaignError?: any;
  error?: unknown;
};

export const IPMatchingContext = createContext<IPMatching>({} as IPMatching);

export const useIPMatchingContext = () =>
  useContext<IPMatching>(IPMatchingContext);

type IPMatchingProviderProps = {
  children: React.ReactNode;
};

export const IPMatchingProvider = ({ children }: IPMatchingProviderProps) => {
  const { campaign, isLoadingCampaign, campaignError } = useAdminCampaignData();
  const [page, setPage] = useState<number>(DEFAULT_PAGE);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const {
    isLoading,
    data: ipMatching,
    error,
    refetch,
    isRefetching
  } = useFetchIPMatching({
    campaignId: campaign?.campaign_id,
    page,
    pageSize
  });

  const status = useMemo(() => {
    if (ipMatching?.enablement_status === undefined) return;

    const key = ipMatching?.enablement_status;

    return getStudyLevelStatus(key);
  }, [ipMatching?.enablement_status]);

  const rootLevelEnablement = useMemo(() => {
    return !!ipMatching?.is_enabled;
  }, [ipMatching?.is_enabled]);

  const matchedIds = useMemo(
    () => (!!ipMatching?.items?.length ? ipMatching.items : []),
    [ipMatching?.items]
  );

  const [updatedIds, setUpdatedIds] = useState<IPMatchedID[]>([]);

  const isLoadingIPMatching = isLoading || isLoadingCampaign;

  const value = useMemo(
    () => ({
      isLoadingIPMatching,
      ipMatching,
      error,
      rootLevelEnablement,
      campaign,
      campaignError,
      matchedIds,
      updatedIds,
      setUpdatedIds,
      refetch,
      isRefetching,
      status,
      page,
      setPage,
      pageSize,
      setPageSize
    }),
    [
      isLoadingIPMatching,
      ipMatching,
      error,
      rootLevelEnablement,
      campaign,
      campaignError,
      matchedIds,
      updatedIds,
      refetch,
      isRefetching,
      status,
      page,
      setPage,
      pageSize,
      setPageSize
    ]
  );

  return (
    <IPMatchingContext.Provider value={value}>
      {children}
    </IPMatchingContext.Provider>
  );
};
